import type { UnknownAction } from '@reduxjs/toolkit'

/**
 * Объявление окна (карточки 252/274, D-092, R11) — нового клиента.
 *
 * Окно объявляется ОДИН раз: адрес, заголовок, место в меню и то, что вкладка
 * помнит. Маршрут, меню, заголовок шапки, чип вкладки и «← назад» собираются
 * отсюда. Содержимое окна дескриптор НЕ содержит: экран пакета получает vm и
 * actions от binding приложения, и связь «окно → binding» задаёт composition.
 * Так пакет объявляет окна, не зная ни RootState, ни приложения (D-051).
 *
 * `S` — состояние модуля пакета (для `capture`), а не RootState.
 */

/** Как окно показано: на своём адресе или модально поверх экрана. Выбирает открывающий. */
export type WindowPresentation = 'page' | 'modal'

/** Размер модального показа. `screen` — весь экран без рамки (сканер). */
export type WindowSize = 'sm' | 'md' | 'lg' | 'full' | 'screen'

/**
 * Область окна. `resource` — окно конкретного ресурса: каждый его адрес
 * обязан содержать `:rid`, и rid входит в identity окна (D-064). Реестр
 * отвергает ресурсное окно без rid при сборке приложения.
 */
export type WindowScope = 'global' | 'resource'

/** Место в меню разделов. Окна без него в меню не показываются. */
export interface WindowMenuPlace {
  group: 'work' | 'settings'
  /** Порядок внутри группы: порядок работы за день, а не алфавит. */
  order: number
  /**
   * Значок пункта — смысловое имя (`catalog`, `sale`), а не компонент: значки
   * рисует оболочка приложения, пакет React-иконок не тащит. Неизвестное
   * имя — общий значок, а не ошибка.
   */
  icon?: string
  /**
   * Ключ права (`platform:testing:search`), без которого пункта в меню нет
   * (RP-FB29). Это только отражение сервера: окно по прямому адресу всё равно
   * говорит «нет доступа», а сервер отвечает 403.
   */
  requires?: string
}

/** Окно раздела подходит ресурсу любого вида — отбор только по праву окна. */
export const ANY_RESOURCE_KIND = '*'

/**
 * Вкладка раздела, которую даёт ресурсное окно пакета вида (UI-SECTIONS): по
 * одной на КАЖДЫЙ доступный ресурс вида `resourceKind` с правом `capability`.
 * Какие ресурсы — решает сервер («мои ресурсы» с правом), поэтому десять
 * источников — десять вкладок без правки меню.
 */
export interface WindowSectionTab {
  /**
   * Вид ресурса в реестре Platform: окно 1С не открывается с rid кабинета AQSI. `ANY_RESOURCE_KIND` — окно не зависит от
   * вида (например, «Дубли»: служба работает с любым источником, у которого есть право): какие ресурсы подходят,
   * решает одно право `capability`.
   */
  resourceKind: string
  /** Право, без которого окно ничего не покажет: по нему отбираются ресурсы. */
  capability: string
  /**
   * Чем вкладка занята: «Каталог», «Касса». Видно, когда у раздела несколько
   * окон одного вида; иначе вкладка называется именем ресурса.
   */
  label: string
  /**
   * Порядок окна среди вкладок раздела: меньше — левее. Без него — после
   * упорядоченных, в порядке реестра. Ресурсы одного окна идут подряд.
   */
  order?: number
}

/**
 * Место окна в разделе меню (UI-SECTIONS). Раздел — бизнес-область
 * («Номенклатура», «Продажа»), а не источник: пункты меню не называют виды
 * ресурсов. Раздел объявляет ровно одно окно-вход (`home`) с `menu`; окна
 * пакетов видов приходят в раздел вкладками (`tab`) или просто относятся к
 * нему (карточка, мастер — меню подсвечивает раздел, вкладок у них нет).
 */
export interface WindowSectionPlace {
  /** Раздел: `nomenclature`, `sale`. Только латиница, цифры и «-». */
  id: string
  /**
   * Окно — вход раздела: пункт меню и адрес раздела. `tab` — подпись его
   * СОБСТВЕННОЙ вкладки («Общий каталог»): вход показывает своё содержимое и
   * стоит первой вкладкой. Без `tab` вход только ведёт во вкладку ресурса.
   * `pickResource` — вход начинается с выбора источника плитками (как в РМК): раздел сам вкладку не открывает,
   * а окно выбранного ресурса показывается под полосой «Источник · Сменить» и только со своими вкладками.
   */
  home?: { tab?: string; pickResource?: boolean }
  /** Окно — вкладка раздела для каждого подходящего ресурса. */
  tab?: WindowSectionTab
  /**
   * Окно — СОБСТВЕННАЯ вкладка раздела, не привязанная к ресурсу («Настройка»): общее окно, одна вкладка на раздел, стоит
   * сразу после вкладки входа. Меню у такого окна нет — в раздел ведёт вход.
   */
  ownTab?: WindowSectionOwnTab
}

export interface WindowSectionOwnTab {
  /** Подпись вкладки. */
  label: string
  /** Порядок среди собственных вкладок раздела: меньше — левее. */
  order?: number
}

/** Параметры окна из адреса: `rid`, ключ сущности. */
export type WindowParams = Readonly<Record<string, string>>

/** Снимок работы окна во вкладке: выбор и фильтры, НЕ данные сервера. */
export type WindowSnapshot = Record<string, unknown>

/**
 * Чьё окно: источник, данные которого оно показывает. Объявляет пакет окна (оболочка вида не знает): `id` — смысловое имя
 * оформления (`common`, `onec`, `aqsi`, `own`), по нему рамка окна берёт оттенок из токенов темы; `label` — слово в строке
 * источника над содержимым («Общий каталог», «1С»). Подпись обязательна: окно различается не только цветом.
 */
export interface WindowSourceMark {
  id: string
  label: string
}

export interface WindowDescriptor<S = unknown> {
  /** Устойчивое имя `<пакет>.<окно>`: `platform.resources`, `onec.product`. */
  kind: string
  scope: WindowScope
  /** Адреса окна; первый — основной. Параметры — `:rid`, `:key`. */
  paths: readonly string[]
  /** Как окно называется в меню: не зависит от состояния. */
  label: string
  /** Чем окно занято сейчас (шапка, чип, «назад»). Не задано — `label`. */
  title?: (params: WindowParams) => string
  size?: WindowSize
  /** Чьё это окно (см. `WindowSourceMark`): рамка помечает его источником и оттенком. Не задано — окно без источника. */
  source?: WindowSourceMark
  /**
   * `page` — страница внутри раздела (экраны «Тестирования»): открывается на своём адресе, как рабочее окно, но
   * пункта меню у неё нет и «← Назад» есть всегда — ведёт туда, откуда её открыли. Не задано — диалог поверх экрана.
   */
  presentation?: 'page'
  menu?: WindowMenuPlace
  /** Раздел меню, к которому окно относится (UI-SECTIONS). */
  section?: WindowSectionPlace
  /**
   * Что вкладка запоминает, когда человек уходит из окна: выбор и фильтры
   * ЭТОГО окна (по его params), никогда не ответы сервера.
   */
  capture?: (moduleState: S, params: WindowParams) => WindowSnapshot
  /** Как вернуть запомненное: обычные действия слайсов пакета. */
  restore?: (snapshot: WindowSnapshot, params: WindowParams) => UnknownAction[]
}

/** Дескриптор, привязанный composition к RootState. */
export interface BoundWindowDescriptor<R> extends Omit<WindowDescriptor, 'capture' | 'restore'> {
  /** Модуль-владелец окна: `platform`, `onec`, `aqsi`. */
  moduleId: string
  capture?: (root: R, params: WindowParams) => WindowSnapshot
  restore?: (snapshot: WindowSnapshot, params: WindowParams) => UnknownAction[]
}

/** Окно на конкретном адресе: дескриптор и разобранные параметры. */
export interface WindowMatch<R> {
  descriptor: BoundWindowDescriptor<R>
  params: WindowParams
  /** Identity окна: `kind` + значения параметров (`onec.product:<rid>/<key>`). */
  identity: string
}
